import React, { useState, useEffect } from "react";
import { Hero, SearchForm, UsersCard, RegisterBanner, GalleryCard, Messages } from '../components';
import { User, defaultUser, defaultQuestionGame, Search, GalleryPhoto, defaultGalleryPhoto, cardTitle, cardTitleUrl, CardTitleType, defaultLocation, QuestionGame, QuestionGameUser, questionGameUpdateFirst, questionGameUpdateSecond, Message } from '../models';
import { getUsers, getQuestionGameUsers, questionGameVote, getMessages } from '../services';

export interface FrontPageProps {
    openInNewTab: boolean;
    userCardVisible: boolean;
    galleryCardVisible: boolean;
    initialState: FrontPageState;
    currentUser: User;
    searchAgeMin: number;
    searchAgeMax: number;
}

export interface FrontPageState {
    loading: boolean;
    search: Search;
    hero: User[];
    online: User[];
    regular: User[];
    newest: User[];
    popular: User[];
    galleryPhotos: GalleryPhoto[];
    questionGame: QuestionGame;
    messages: Message[];
}

export const fillEmptyPhotos = (state: FrontPageState): FrontPageState => {
    return {
        ...state,
        hero: [...state.hero, ...Array(9).fill(defaultUser)].slice(0, 9) as User[],
        online: [...state.online, ...Array(4 - state.online.length).fill(defaultUser)].slice(0, 4) as User[],
        regular: [...state.regular, ...Array(4).fill(defaultUser)].slice(0, 4) as User[],
        newest: [...state.newest, ...Array(4).fill(defaultUser)].slice(0, 4) as User[],
        popular: [...state.popular, ...Array(4).fill(defaultUser)].slice(0, 4) as User[],
        galleryPhotos: [...state.galleryPhotos, ...Array(8).fill(defaultGalleryPhoto)].slice(0, 8) as GalleryPhoto[],
    };
};

export const FrontPage = ({ initialState, currentUser, searchAgeMin, searchAgeMax, openInNewTab, userCardVisible, galleryCardVisible }: FrontPageProps) => {
    const [state, setState] = useState<FrontPageState>(fillEmptyPhotos(initialState));

    return <>
        <div className="app-card">
            <h1>Populiariausia pažinčių svetainė Lietuvoje!</h1>
            <div className="app-collage-container">
                <Hero
                    openInNewTab={openInNewTab}
                    users={state.hero}
                    loading={state.loading}
                    questionGame={state.questionGame}
                    onQuestionGameVote={async (userId, type) => {
                        if (state.questionGame.first.userId === userId) {
                            setState(questionGameUpdateFirst(state, { ...state.questionGame.first, loading: true }));
                            const user = await questionGameVote(type, userId, state.questionGame.second.userId, state.search.ageFrom, state.search.ageTo, state.questionGame.first.gender, state.search.location.id);
                            setState(questionGameUpdateFirst(state, user));
                        }

                        if (state.questionGame.second.userId === userId) {
                            setState(questionGameUpdateSecond(state, { ...state.questionGame.first, loading: true }));
                            const user = await questionGameVote(type, userId, state.questionGame.first.userId, state.search.ageFrom, state.search.ageTo, state.questionGame.second.gender, state.search.location.id);
                            setState(questionGameUpdateSecond(state, user));
                        }
                    }} onResetFilter={() => {
                        const search = {
                            ...state.search,
                            ageFrom: state.search.ageFrom - 5 < 18 ? 18 : state.search.ageFrom - 5,
                            ageTo: state.search.ageTo + 5 > 80 ? 80 : state.search.ageTo + 5,
                            location: defaultLocation
                        };
                        setState({ ...state, search: search, loading: true });
                    }} />
                <div className="app-right-column">
                    {currentUser === null ? <RegisterBanner /> : <Messages messages={state.messages} />}
                    <SearchForm search={state.search}
                        ageMin={searchAgeMin}
                        ageMax={searchAgeMax}
                        onChange={(search) => {
                            Promise.all([getUsers(search), getQuestionGameUsers(search)])
                                .then(([newState, questionGame]) => {
                                    return {
                                        ...newState,
                                        questionGame: questionGame,
                                        messages: state.messages,
                                        galleryPhotos: state.galleryPhotos
                                    };
                                }).then(fillEmptyPhotos).then(setState);
                        }} />
                </div>
            </div>
        </div>

        {userCardVisible ?
            <UsersCard
                openInNewTab={openInNewTab}
                title={cardTitle(CardTitleType.Regular, state.search)}
                titleUrl={cardTitleUrl(CardTitleType.Regular, state.search)}
                users={state.regular}
                loading={state.loading} /> : null}


        {userCardVisible ? <UsersCard
            openInNewTab={openInNewTab}
            title={cardTitle(CardTitleType.Online, state.search)}
            titleUrl={cardTitleUrl(CardTitleType.Online, state.search)}
            users={state.online}
            loading={state.loading} /> : null}

        {userCardVisible ? <UsersCard
            openInNewTab={openInNewTab}
            title={cardTitle(CardTitleType.Newest, state.search)}
            titleUrl={cardTitleUrl(CardTitleType.Newest, state.search)}
            users={state.newest} loading={state.loading} /> : null}

        {userCardVisible ? <UsersCard
            openInNewTab={openInNewTab}
            title={cardTitle(CardTitleType.Popular, state.search)}
            titleUrl={cardTitleUrl(CardTitleType.Popular, state.search)}
            users={state.popular}
            loading={state.loading} /> : null}

        {galleryCardVisible ? <GalleryCard
            openInNewTab={openInNewTab}
            title={cardTitle(CardTitleType.GalleryPhotos, state.search, currentUser)}
            titleUrl="https://pazintys.draugas.lt/foto/paieska/nuotraukos.cfm?megstamiausios=1"
            photos={state.galleryPhotos}
            loading={state.loading} /> : null}
    </>;
};